import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from 'shared/utils'
import { Datepicker } from '@wmgtech/legato'
import { MonthPicker } from './MonthPicker'
import { WeekPicker } from './WeekPicker'

import {
  formatRange,
  weekPickerFormat,
  createCustomOption,
  parsePicker,
  monthPickerFormat,
  formatCustomRange,
  localeFormat
} from 'shared/utils/formatDate'

import { DateRangePickerProps } from './DateRangePicker.props'

export const DateRangePicker: FC<DateRangePickerProps> = ({ onSelect, value, type, placeholder }) => {
  const parsedValue = useMemo(() => value?.value?.includes('from') && parsePicker(value), [value])
  const pickerValue = useMemo(
    () => (parsedValue && parsedValue?.type === type ? parsedValue.from : undefined),
    [parsedValue, type]
  )
  const initialStartDate = parsedValue && parsedValue.type === 'range' ? parsedValue.from : null
  const initialEndDate = parsedValue && parsedValue.type === 'range' ? parsedValue.to : null
  const isDateRangeSelected = !!parsedValue && parsedValue?.type === type
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)

  useEffect(() => {
    if (!parsedValue || parsedValue?.type !== type) {
      setStartDate(null)
      setEndDate(null)
    }
  }, [value, parsedValue, type])

  const selectCustomOptionValue = useCallback(
    (selectedValue) => {
      if (type === 'range') {
        const [start, end] = selectedValue
        setStartDate(start)
        setEndDate(end)
        if (start && end) {
          onSelect(createCustomOption(formatCustomRange(start, end, type)))()
        }
      } else {
        setStartDate(selectedValue)
        onSelect(createCustomOption(formatRange(selectedValue, type)))()
      }
    },
    [onSelect, type]
  )

  if (type === 'month') {
    return (
      <MonthPicker
        onChange={selectCustomOptionValue}
        isSelected={isDateRangeSelected}
        value={pickerValue ? monthPickerFormat(pickerValue) : ''}
        startDate={startDate}
      />
    )
  }

  if (type === 'week') {
    return (
      <WeekPicker
        onChange={selectCustomOptionValue}
        isSelected={isDateRangeSelected}
        value={pickerValue ? weekPickerFormat(pickerValue) : ''}
        startDate={startDate}
      />
    )
  }

  if (type === 'range') {
    return (
      <Datepicker
        dateFormat={localeFormat()}
        className={cn('items-list__item', { 'items-list__item--checked': isDateRangeSelected })}
        onChange={selectCustomOptionValue}
        placeholder={placeholder}
        size="md"
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        selectsRange
      />
    )
  }

  return null
}
